import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Heart, Mail, Lock, User, MapPin, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useLanguage } from '../i18n/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Auth() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode') || 'login';
  const [isLogin, setIsLogin] = useState(mode === 'login');
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    age: '',
    gender: 'homme',
    location: '',
    acceptedTerms: false
  });

  const [emailValidation, setEmailValidation] = useState({ checking: false, valid: null, message: '' });

  useEffect(() => {
    setIsLogin(mode === 'login');
  }, [mode]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleEmailBlur = async () => {
    if (!isLogin && formData.email && formData.email.includes('@')) {
      setEmailValidation({ checking: true, valid: null, message: '' });
      
      try {
        const formDataToSend = new FormData();
        formDataToSend.append('email', formData.email);
        
        const response = await axios.post(`${API}/auth/verify-email`, formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        setEmailValidation({
          checking: false,
          valid: response.data.valid,
          message: response.data.message
        });
      } catch (error) {
        setEmailValidation({ checking: false, valid: null, message: '' });
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        const response = await axios.post(`${API}/auth/login`, {
          email: formData.email,
          password: formData.password
        });

        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        toast.success('Connexion réussie !');
        navigate(`/${language}/dashboard`);
      } else {
        // Signup
        if (parseInt(formData.age) < 18) {
          toast.error('Vous devez avoir au moins 18 ans pour vous inscrire');
          setLoading(false);
          return;
        }

        if (!formData.acceptedTerms) {
          toast.error('Vous devez accepter les conditions générales');
          setLoading(false);
          return;
        }

        const response = await axios.post(`${API}/auth/register`, {
          email: formData.email,
          password: formData.password,
          name: formData.name,
          age: parseInt(formData.age),
          gender: formData.gender,
          location: formData.location,
          accepted_terms: formData.acceptedTerms
        });

        if (response.data.needs_payment) {
          toast.info(response.data.message);
          navigate(`/${language}/payment`);
        } else {
          localStorage.setItem('token', response.data.token);
          localStorage.setItem('user', JSON.stringify(response.data.user));
          toast.success('Inscription réussie !');
          navigate(`/${language}/questionnaire`);
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12">
      <div className="absolute inset-0 z-0">
        <div className="absolute top-20 right-10 w-96 h-96 bg-pink-300 rounded-full opacity-20 blur-3xl" />
        <div className="absolute bottom-20 left-10 w-72 h-72 bg-orange-300 rounded-full opacity-20 blur-3xl" />
      </div>

      <div className="relative z-10 w-full max-w-md">
        <div className="glass-card p-8">
          <div className="text-center mb-8">
            <Heart className="w-12 h-12 mx-auto mb-4 text-pink-500 fill-pink-500" />
            <h2 className="text-3xl font-bold gradient-text mb-2">
              {isLogin ? 'Bon retour !' : 'Rejoignez A-More AI'}
            </h2>
            <p className="text-gray-600">
              {isLogin ? 'Connectez-vous pour trouver l\'amour' : 'Créez votre compte gratuitement'}
            </p>
          </div>

          <form data-testid="auth-form" onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <User className="w-4 h-4 inline mr-2" />
                    Nom complet
                  </label>
                  <input
                    data-testid="auth-name-input"
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                    placeholder="Votre nom"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Calendar className="w-4 h-4 inline mr-2" />
                    Âge
                  </label>
                  <input
                    data-testid="auth-age-input"
                    type="number"
                    name="age"
                    value={formData.age}
                    onChange={handleChange}
                    required
                    min="18"
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                    placeholder="Votre âge"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Genre
                  </label>
                  <select
                    data-testid="auth-gender-select"
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                  >
                    <option value="homme">Homme</option>
                    <option value="femme">Femme</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <MapPin className="w-4 h-4 inline mr-2" />
                    Localisation
                  </label>
                  <input
                    data-testid="auth-location-input"
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                    placeholder="Ville, Pays"
                  />
                </div>
              </>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Mail className="w-4 h-4 inline mr-2" />
                Email
              </label>
              <div className="relative">
                <input
                  data-testid="auth-email-input"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={handleEmailBlur}
                  required
                  className={`w-full px-4 py-3 rounded-lg border focus:ring-2 focus:border-transparent ${
                    emailValidation.valid === false
                      ? 'border-red-500 focus:ring-red-500'
                      : emailValidation.valid === true
                      ? 'border-green-500 focus:ring-green-500'
                      : 'border-gray-300 focus:ring-pink-500'
                  }`}
                  placeholder="votre@email.com"
                />
                {emailValidation.checking && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-pink-500"></div>
                  </div>
                )}
              </div>
              {emailValidation.message && !isLogin && (
                <p className={`text-sm mt-1 ${
                  emailValidation.valid ? 'text-green-600' : 'text-red-600'
                }`}>
                  {emailValidation.message}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Lock className="w-4 h-4 inline mr-2" />
                Mot de passe
              </label>
              <input
                data-testid="auth-password-input"
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="••••••••"
              />
            </div>

            {!isLogin && (
              <div className="flex items-start gap-3">
                <input
                  data-testid="auth-terms-checkbox"
                  type="checkbox"
                  id="acceptTerms"
                  checked={formData.acceptedTerms}
                  onChange={(e) => setFormData({ ...formData, acceptedTerms: e.target.checked })}
                  className="mt-1 w-4 h-4 text-pink-600 border-gray-300 rounded focus:ring-pink-500"
                  required
                />
                <label htmlFor="acceptTerms" className="text-sm text-gray-700">
                  J'accepte les{' '}
                  <button
                    type="button"
                    onClick={() => window.open('/terms', '_blank')}
                    className="text-pink-600 hover:text-pink-700 underline font-medium"
                  >
                    conditions générales d'utilisation
                  </button>
                </label>
              </div>
            )}

            <button
              data-testid="auth-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full btn-primary py-4"
            >
              {loading ? 'Chargement...' : isLogin ? 'Se connecter' : 'Créer mon compte'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              data-testid="auth-toggle-btn"
              onClick={() => {
                setIsLogin(!isLogin);
                navigate(isLogin ? `/${language}/auth?mode=signup` : `/${language}/auth?mode=login`);
              }}
              className="text-pink-600 hover:text-pink-700 font-medium"
            >
              {isLogin ? 'Pas encore de compte ? Inscrivez-vous' : 'Déjà un compte ? Connectez-vous'}
            </button>
          </div>

          {!isLogin && (
            <p className="text-xs text-gray-500 mt-6 text-center">
              En cas de rencontre réussie, nous vous encourageons à faire un don pour soutenir l'application.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
